import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  BookOpen, Users, CreditCard, Video, FileText, Settings, 
  DollarSign, Share2, BarChart3, HelpCircle, ChevronRight,
  GraduationCap, Sparkles, Headphones, Award, MessageSquare
} from 'lucide-react';

const KnowledgeBase = () => {
  const studentGuides = [
    {
      icon: BookOpen,
      title: "Getting Started as a Student",
      description: "Learn how to create an account, browse courses, and make your first purchase.",
      steps: [
        "Create a free account by clicking 'Get Started'",
        "Browse the course catalog or search for specific topics",
        "Click on a course to view details, curriculum, and preview lessons",
        "Click 'Buy This Course' to purchase with a secure checkout",
        "Access your purchased courses from your Dashboard"
      ]
    },
    {
      icon: Video,
      title: "Taking Courses",
      description: "How to navigate courses, track progress, and complete lessons.",
      steps: [
        "Go to Dashboard and click 'Continue Learning' on any course",
        "Use the sidebar to navigate between lessons",
        "Mark lessons as complete using the checkbox",
        "Your progress is automatically saved",
        "Complete quizzes to test your knowledge"
      ]
    },
    {
      icon: Share2,
      title: "Referral Program",
      description: "Earn rewards by inviting friends to AISkillFlow.",
      steps: [
        "Find your unique referral link in your Dashboard",
        "Share the link with friends via email or social media",
        "When they sign up using your link, they get 20% off",
        "You earn $10 credit when they make their first purchase",
        "Credits accumulate and can be used on future purchases"
      ]
    }
  ];

  const academyGuides = [
    {
      icon: GraduationCap,
      title: "Joining AI Academy",
      description: "Everything you need to know about the AI Academy membership.",
      steps: [
        "Navigate to AI Academy from the top menu",
        "Browse all 18 courses covering ChatGPT, Midjourney, Claude, and more",
        "Choose a membership plan: Monthly ($17/mo) or Yearly ($170/yr, save 17%)",
        "Complete checkout via Stripe to unlock all courses instantly",
        "Access your member dashboard to track progress across all courses"
      ]
    },
    {
      icon: BookOpen,
      title: "Taking AI Academy Courses",
      description: "How to navigate lessons, modules, and maximize your learning.",
      steps: [
        "Each course has 5 modules with 4 lessons each (20 lessons per course)",
        "Start from Module 1, Lesson 1 and progress sequentially",
        "Each lesson includes rich content, copy-paste prompts, and pro tips",
        "Use the 'Copy Prompt' button on code blocks to grab prompts instantly",
        "Click 'Next' to move to the next lesson automatically",
        "The first 2 lessons of each course are free to preview!"
      ]
    },
    {
      icon: Sparkles,
      title: "Using AISkillFlow AI Coach",
      description: "Get personalized help from your AI learning assistant.",
      steps: [
        "Click 'AI Coach' from the AI Academy dropdown in the navbar",
        "Or click 'Ask AI Coach' on any lesson sidebar for lesson-specific help",
        "Use quick buttons: 'Explain this lesson', 'Give me examples', etc.",
        "Ask follow-up questions — the coach remembers your conversation",
        "Start new chats for different topics using the sidebar",
        "The AI Coach knows all course content and gives actionable advice"
      ]
    },
    {
      icon: Headphones,
      title: "Listening to Audio Lessons",
      description: "Every lesson has a full audio version you can listen to.",
      steps: [
        "Open any lesson and find the Play button at the top of the lesson",
        "Click the Play button once to generate the audio (first time only)",
        "Allow 15-30 seconds for the audio to be created",
        "Once ready, click the Play button again to start listening",
        "The audio will read the entire lesson from start to finish",
        "After the first listen, the audio is saved and plays instantly next time",
        "Download the lesson checklist for a quick reference sheet",
        "All audio and resources are included with your membership"
      ]
    },
    {
      icon: Award,
      title: "Quizzes & Certificates",
      description: "Test your knowledge and earn certificates.",
      steps: [
        "Each lesson has a 4-question quiz to test what you learned",
        "At the end of each course, take a 10-question final quiz",
        "Score 70% or higher on the final quiz to earn a certificate",
        "Download your certificate as a PDF from the completion screen",
        "Certificates show your name, course title, and completion date"
      ]
    }
  ];

  const instructorGuides = [
    {
      icon: Settings,
      title: "Becoming an Instructor",
      description: "How to set up your instructor account and start teaching.",
      steps: [
        "Register and select 'Teach' during signup",
        "Go to Instructor Pricing and choose a plan (Starter, Pro, or Unlimited)",
        "Complete payment to activate your instructor subscription",
        "You can now create and publish courses!"
      ]
    },
    {
      icon: FileText,
      title: "Creating a Course",
      description: "Step-by-step guide to creating engaging courses.",
      steps: [
        "From Instructor Dashboard, click 'Create Course'",
        "Enter title, description (or use AI Generate), category, and price",
        "Save the course to unlock the lesson editor",
        "Add lessons: Video (YouTube/Vimeo URL), Text (HTML content), or Quiz (JSON format)",
        "Mark some lessons as 'Preview' to let students sample before buying",
        "Click 'Publish' when ready to go live"
      ]
    },
    {
      icon: Video,
      title: "Adding Video Lessons",
      description: "Best practices for video content.",
      steps: [
        "Upload your video to YouTube (unlisted) or Vimeo",
        "Copy the video URL",
        "In the lesson editor, select 'Video' as content type",
        "Paste the URL in the content field",
        "Set the duration in minutes",
        "Consider making the first lesson a free preview"
      ]
    },
    {
      icon: DollarSign,
      title: "Pricing & Earnings",
      description: "Understanding the revenue model.",
      steps: [
        "Set your own course prices (we recommend $19-$99)",
        "You keep 80% of every sale",
        "AISkillFlow takes a 20% platform fee",
        "View earnings in Analytics dashboard",
        "Request payouts when balance reaches $10 minimum"
      ]
    },
    {
      icon: BarChart3,
      title: "Analytics & Growth",
      description: "Track performance and grow your audience.",
      steps: [
        "Access Analytics from Instructor Dashboard",
        "Monitor views, sales, and conversion rates",
        "Create coupon codes to drive sales for your courses",
        "Share your courses on social media to attract students",
        "Respond to student questions to improve engagement"
      ]
    }
  ];

  const GuideCard = ({ guide }) => (
    <Card className="border-slate-200 h-full">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <guide.icon className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{guide.title}</h3>
            <p className="text-slate-600 text-sm mb-4">{guide.description}</p>
            <ol className="space-y-2">
              {guide.steps.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-700">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="knowledge-base">
      <Navbar />

      {/* Hero */}
      <div className="pt-24 pb-12 bg-gradient-to-br from-primary/5 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <BookOpen className="w-16 h-16 text-primary mx-auto mb-4" />
          <h1 className="font-heading text-4xl font-bold text-slate-900 mb-4">Knowledge Base</h1>
          <p className="text-lg text-slate-600">Everything you need to know about AISkillFlow</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* AI Academy Guides */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <Badge className="bg-indigo-100 text-indigo-800">AI Academy</Badge>
            <h2 className="font-heading text-2xl font-semibold text-slate-900">AI Academy Guides</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {academyGuides.map((guide, i) => (
              <GuideCard key={i} guide={guide} />
            ))}
          </div>
        </div>

        {/* Student Guides */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <Badge className="bg-green-100 text-green-800">For Students</Badge>
            <h2 className="font-heading text-2xl font-semibold text-slate-900">Student Guides</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {studentGuides.map((guide, i) => (
              <GuideCard key={i} guide={guide} />
            ))}
          </div>
        </div>

        {/* Instructor Guides */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <Badge className="bg-purple-100 text-purple-800">For Instructors</Badge>
            <h2 className="font-heading text-2xl font-semibold text-slate-900">Instructor Guides</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {instructorGuides.map((guide, i) => (
              <GuideCard key={i} guide={guide} />
            ))}
          </div>
        </div>

        {/* Platform Features */}
        <div className="mb-16">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-8">Platform Features</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-slate-200 p-6 text-center">
              <GraduationCap className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">AI Academy</h3>
              <p className="text-sm text-slate-600">18 courses, 360 lessons on ChatGPT, Midjourney, Claude & more</p>
            </Card>
            <Card className="border-slate-200 p-6 text-center">
              <Sparkles className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">AI Learning Coach</h3>
              <p className="text-sm text-slate-600">Personal AI assistant to explain lessons and give advice</p>
            </Card>
            <Card className="border-slate-200 p-6 text-center">
              <Headphones className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">Audio Narration</h3>
              <p className="text-sm text-slate-600">Listen to every lesson with professional AI narration</p>
            </Card>
            <Card className="border-slate-200 p-6 text-center">
              <Video className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">Video Lessons</h3>
              <p className="text-sm text-slate-600">Stream high-quality video content from YouTube or Vimeo</p>
            </Card>
            <Card className="border-slate-200 p-6 text-center">
              <CreditCard className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">Secure Payments</h3>
              <p className="text-sm text-slate-600">Safe checkout powered by Stripe</p>
            </Card>
            <Card className="border-slate-200 p-6 text-center">
              <Users className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">Referral Program</h3>
              <p className="text-sm text-slate-600">Earn rewards by inviting friends</p>
            </Card>
          </div>
        </div>

        {/* Need More Help */}
        <Card className="border-slate-200 bg-gradient-to-r from-primary/5 to-purple-50 p-8 text-center">
          <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Still have questions?</h3>
          <p className="text-slate-600 mb-4">Our support team is here to help</p>
          <Link to="/help" className="inline-flex items-center gap-2 text-primary font-medium hover:underline">
            Visit Help Center <ChevronRight className="w-4 h-4" />
          </Link>
        </Card>
      </div>
    </div>
  );
};

export default KnowledgeBase;
